<template>
  <div class="full-calendar">
    <!-- Google Calendar Style Toolbar -->
    <div class="google-calendar-toolbar">
      <div class="toolbar-left">
        <button 
          @click="$emit('add-event')" 
          class="create-btn"
          :disabled="loading"
        >
          <i class="fas fa-plus"></i>
          <span class="create-text">Create</span>
        </button>
        
        <button 
          @click="goToToday" 
          class="today-btn"
          :disabled="loading"
        >
          Today
        </button>
        
        <div class="nav-controls">
          <button 
            @click="previousMonth" 
            class="nav-btn"
            :disabled="loading"
            title="Previous"
          >
            <i class="fas fa-chevron-left"></i>
          </button>
          <button 
            @click="nextMonth" 
            class="nav-btn"
            :disabled="loading"
            title="Next"
          >
            <i class="fas fa-chevron-right"></i>
          </button>
        </div>
        
        <h1 class="calendar-title">
          {{ monthNames[currentMonth] }} {{ currentYear }}
        </h1>
      </div>
      
      <div class="toolbar-right">
        <div class="view-selector">
          <button 
            @click="currentView = 'day'"
            :class="['view-btn', { active: currentView === 'day' }]"
          >
            Day
          </button>
          <button 
            @click="currentView = 'week'"
            :class="['view-btn', { active: currentView === 'week' }]"
          >
            Week
          </button>
          <button 
            @click="currentView = 'month'"
            :class="['view-btn', { active: currentView === 'month' }]"
          >
            Month
          </button>
        </div>
      </div>
    </div>

    <!-- Month View -->
    <div v-if="currentView === 'month'" class="month-view">
      <!-- Google Style Weekday Headers -->
      <div class="google-weekdays-header">
        <div 
          v-for="day in weekdays" 
          :key="day.short" 
          class="google-weekday-cell"
        >
          <span class="weekday-label">{{ day.short }}</span>
        </div>
      </div>

      <!-- Calendar Grid -->
      <div class="calendar-grid">
        <div
          v-for="date in calendarDates"
          :key="`${date.year}-${date.month}-${date.day}`"
          :class="[
            'date-cell',
            {
              'other-month': date.isOtherMonth,
              'today': date.isToday,
              'past': date.isPast,
              'has-events': date.events.length > 0,
              'weekend': date.isWeekend,
              'selected': isDateSelected(date)
            }
          ]"
          @click="handleDateClick(date)"
          @dragover.prevent="handleDragOver(date, $event)"
          @dragenter.prevent="handleDragEnter(date, $event)" 
          @dragleave="handleDragLeave(date, $event)"
          @drop="handleDrop(date, $event)"
        >
          <!-- Google Style Day Header -->
          <div class="google-day-header">
            <span :class="['google-day-number', { 'today-number': date.isToday }]">
              {{ date.day }}
            </span>
          </div>

          <!-- Google Style Events -->
          <div class="google-events-container">
            <div
              v-for="event in getEventsForDisplay(date.events)"
              :key="event.id"
              :class="[
                'google-event-item',
                { 
                  'inactive': !event.active,
                  'dragging': draggedEvent && draggedEvent.id === event.id
                }
              ]"
              @click="handleEventItemClick($event, event, date)"  
              :title="getEventTooltip(event)"
              :style="getGoogleEventStyle(event)"
              draggable="true"
              @dragstart="handleDragStart(event, $event)"
              @dragend="handleDragEnd"
            >
              <div class="google-event-content">
                <div class="google-event-time" v-if="event.additionalProp1?.start_time">
                  {{ formatTime(event.additionalProp1.start_time) }}
                </div>
                <div class="google-event-title">{{ event.title }}</div>
              </div>
            </div>
            
            <!-- More events indicator -->
            <div 
              v-if="date.events.length > getMaxEventsPerDay()" 
              class="google-more-events"
              @click.stop="showMoreEvents(date)"
            >
              {{ date.events.length - getMaxEventsPerDay() }} more
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Week View -->
    <div v-if="currentView === 'week'" class="week-view">
      <div class="week-header">
        <div class="time-gutter"></div>
        <div
          v-for="date in currentWeekDates"
          :key="`week-${date.date}`"
          :class="['week-day-header', { 'today': date.isToday }]"
        >
          <div class="week-day-name">{{ weekdays[date.dayOfWeek].short }}</div>
          <div class="week-day-number">{{ date.day }}</div>
        </div>
      </div>
      
      <div class="week-grid">
        <div class="time-slots">
          <div
            v-for="hour in 24"
            :key="`hour-${hour-1}`"
            class="time-slot"
          >
            <span class="time-label">{{ formatHour(hour-1) }}</span>
          </div>
        </div>
        
        <div class="week-days">
          <div
            v-for="date in currentWeekDates"
            :key="`week-day-${date.date}`"
            class="week-day-column"
          >
            <div
              v-for="hour in 24"
              :key="`slot-${date.date}-${hour-1}`"
              :class="['time-slot-cell', { 'current-hour': isCurrentHour(hour-1, date) }]"
              @click="addEventToDateTime(date, hour-1)"
            >
              <!-- Events for this time slot -->
              <div
                v-for="event in getEventsForTimeSlot(date.events, hour-1)"
                :key="event.id"
                class="week-event-item"
                @click="handleEventItemClick($event, event, date)"
                :title="getEventTooltip(event)"
                :style="getEventStyle(event)"
              >
                <div class="week-event-content">
                  <div class="week-event-time">
                    {{ formatTime(event.additionalProp1?.start_time) }}
                  </div>
                  <div class="week-event-title">{{ event.title }}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Day View -->
    <div v-if="currentView === 'day'" class="day-view">
      <div class="day-header">
        <div class="day-navigation">
          <button @click="previousDay" class="nav-button">
            <i class="fas fa-chevron-left"></i>
          </button>
          <h3 class="day-title">
            {{ formatDayTitle(selectedDate) }}
          </h3>
          <button @click="nextDay" class="nav-button">
            <i class="fas fa-chevron-right"></i>
          </button>
        </div>
      </div>
      
      <div class="day-grid">
        <div class="time-slots">
          <div
            v-for="hour in 24"
            :key="`day-hour-${hour-1}`"
            class="time-slot"
          >
            <span class="time-label">{{ formatHour(hour-1) }}</span>
          </div>
        </div>
        
        <div class="day-column">
          <div
            v-for="hour in 24"
            :key="`day-slot-${hour-1}`"
            :class="['time-slot-cell', { 'current-hour': isCurrentHour(hour-1, selectedDate) }]"
            @click="addEventToDateTime(selectedDate, hour-1)"
          >
            <!-- Events for this time slot -->
            <div
              v-for="event in getEventsForTimeSlot(getEventsForDate(selectedDate), hour-1)"
              :key="event.id"
              class="day-event-item"
              @click="handleEventItemClick($event, event, selectedDate)"
              :title="getEventTooltip(event)"
              :style="getEventStyle(event)"
            >
              <div class="day-event-content">
                <div class="day-event-time">
                  {{ formatTimeRange(event.additionalProp1?.start_time, event.additionalProp1?.end_time) }}
                </div>
                <div class="day-event-title">{{ event.title }}</div>
                <div class="day-event-description" v-if="event.additionalProp1?.description" v-html="event.additionalProp1.description">
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="loading-overlay">
      <div class="loading-content">
        <div class="spinner"></div>
        <p class="loading-text">Loading events...</p>
      </div>
    </div>

    <!-- Event Details Modal (if needed) -->
    <div v-if="showEventModal" class="event-modal-overlay" @click="closeEventModal">
      <div class="event-modal" @click.stop>
        <div class="modal-header">
          <h5 class="modal-title">Event Details</h5>
          <button @click="closeEventModal" class="close-btn">
            <i class="fas fa-times"></i>
          </button>
        </div>
        <div class="modal-body" v-if="selectedEvent">
          <div class="event-detail">
            <strong>Title:</strong> {{ selectedEvent.title }}
          </div>
          <div class="event-detail" v-if="selectedEvent.additionalProp1?.date">
            <strong>Date:</strong> {{ formatDate(selectedEvent.additionalProp1.date) }}
          </div>
          <div class="event-detail" v-if="selectedEvent.additionalProp1?.start_time || selectedEvent.additionalProp1?.end_time">
            <strong>Time:</strong> 
            {{ formatTimeRange(selectedEvent.additionalProp1?.start_time, selectedEvent.additionalProp1?.end_time) }}
          </div>
          <div class="event-detail" v-if="selectedEvent.additionalProp1?.description">
            <strong>Description:</strong>
            <div v-html="selectedEvent.additionalProp1.description"></div>
          </div>
        </div>
        <div class="modal-footer">
          <button @click="editEvent(selectedEvent)" class="btn btn-primary">
            Edit Event
          </button>
          <button @click="closeEventModal" class="btn btn-secondary ml-2">
            Close
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'MonthlyCalendar',
  props: {
    pages: {
      type: Array,
      default: () => []
    },
    month: {
      type: Number,
      default: () => new Date().getMonth()
    },
    year: {
      type: Number,
      default: () => new Date().getFullYear()
    },
    loading: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      currentMonth: this.month,
      currentYear: this.year,
      currentView: 'month', // 'month', 'week', 'day'
      selectedDate: new Date(),
      showEventModal: false,
      selectedEvent: null,
      draggedEvent: null,
      isDragging: false,
      weekdays: [
        { full: 'Monday', short: 'Mon' },
        { full: 'Tuesday', short: 'Tue' },
        { full: 'Wednesday', short: 'Wed' },
        { full: 'Thursday', short: 'Thu' },
        { full: 'Friday', short: 'Fri' },
        { full: 'Saturday', short: 'Sat' },
        { full: 'Sunday', short: 'Sun' }
      ],
      monthNames: [
        'January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December'
      ]
    }
  },
  computed: {
    // Reactive key to force recomputation when event properties change
    eventCacheKey() {
      return this.pages.map(event => 
        `${event.id}-${event.additionalProp1?.date}-${event.additionalProp1?.color}-${event.title}-${event.active}`
      ).join('|')
    },
    filteredEvents() {
      // Reference the cache key to ensure reactivity
      this.eventCacheKey
      return this.pages.filter(event => 
        event.active !== false && 
        event.deleted !== true &&
        (event.additionalProp1?.date || event.date)
      )
    },
    eventsGroupedByDate() {
      const grouped = {}
      
      this.filteredEvents.forEach(event => {
        const eventDate = event.additionalProp1?.date || event.date
        if (eventDate) {
          const dateKey = eventDate.split('T')[0] // Handle ISO dates
          if (!grouped[dateKey]) {
            grouped[dateKey] = []
          }
          grouped[dateKey].push(event)
        }
      })
      
      // Sort events by start time for each date
      Object.keys(grouped).forEach(date => {
        grouped[date].sort((a, b) => {
          const timeA = a.additionalProp1?.start_time || '00:00'
          const timeB = b.additionalProp1?.start_time || '00:00'
          return timeA.localeCompare(timeB)
        })
      })
      
      return grouped
    },
    calendarDates() {
      const dates = []
      // Use local date to avoid timezone issues
      const today = new Date()
      const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`
      
      // First day of the month
      const firstDay = new Date(this.currentYear, this.currentMonth, 1)
      
      // Calculate the first Monday to show (may be from previous month)
      const startDate = new Date(firstDay)
      const dayOfWeek = (firstDay.getDay() + 6) % 7 // Convert Sunday=0 to Monday=0
      startDate.setDate(firstDay.getDate() - dayOfWeek)
      
      // Generate 42 days (6 weeks × 7 days) to fill the calendar grid
      for (let i = 0; i < 42; i++) {
        const date = new Date(startDate)
        date.setDate(startDate.getDate() + i)
        
        const dateStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
        const isCurrentMonth = date.getMonth() === this.currentMonth
        const isToday = dateStr === todayStr
        const isPast = dateStr < todayStr
        const isWeekend = date.getDay() === 0 || date.getDay() === 6
        
        dates.push({
          day: date.getDate(),
          month: date.getMonth(),
          year: date.getFullYear(),
          date: dateStr,
          dateObj: date,
          dayOfWeek: (date.getDay() + 6) % 7, // Monday = 0
          isOtherMonth: !isCurrentMonth,
          isToday: isToday,
          isPast: isPast,
          isWeekend: isWeekend,
          events: this.eventsGroupedByDate[dateStr] || []
        })
      }
      
      // Filter out the last week if ALL days in that week belong to the next month
      // Keep it if at least one day belongs to the current month
      if (dates.length === 42) {
        const lastWeek = dates.slice(35, 42) // Last 7 days
        const hasCurrentMonthDay = lastWeek.some(date => !date.isOtherMonth)
        
        if (!hasCurrentMonthDay) {
          // Remove the last week entirely
          dates.splice(35, 7)
          console.log(`Removed last week - all days belong to next month`)
        }
      }      
      // Debug: log the dates to verify we have 42 days
      console.log(`Generated ${dates.length} dates for ${this.monthNames[this.currentMonth]} ${this.currentYear}:`, 
        dates.map(d => `${d.date} (${d.isOtherMonth ? 'other' : 'current'})`))
      
      return dates
    },

    currentWeekDates() {
      const startOfWeek = new Date(this.selectedDate)
      const dayOfWeek = (startOfWeek.getDay() + 6) % 7 // Monday = 0
      startOfWeek.setDate(startOfWeek.getDate() - dayOfWeek)
      
      const weekDates = []
      for (let i = 0; i < 7; i++) {
        const date = new Date(startOfWeek)
        date.setDate(startOfWeek.getDate() + i)
        const dateStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
        const today = new Date()
        const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`
        
        weekDates.push({
          day: date.getDate(),
          month: date.getMonth(),
          year: date.getFullYear(),
          date: dateStr,
          dateObj: date,
          dayOfWeek: i,
          isToday: dateStr === todayStr,
          events: this.eventsGroupedByDate[dateStr] || []
        })
      }
      
      return weekDates
    }
  },
  methods: {
    formatTime(timeString) {
      if (!timeString) return ''
      
      // Handle both HH:MM and HH:MM:SS formats
      const [hours, minutes] = timeString.split(':')
      const hour = parseInt(hours)
      const minute = minutes || '00'
      
      // Convert to 12-hour format
      if (hour === 0) return `12:${minute} AM`
      if (hour < 12) return `${hour}:${minute} AM`
      if (hour === 12) return `12:${minute} PM`
      return `${hour - 12}:${minute} PM`
    },
    getEventTooltip(event) {
      const title = event.title
      const startTime = event.additionalProp1?.start_time
      const endTime = event.additionalProp1?.end_time
      const description = event.additionalProp1?.description
      
      let tooltip = title
      
      if (startTime || endTime) {
        const timeRange = [startTime, endTime]
          .filter(Boolean)
          .map(this.formatTime)
          .join(' - ')
        tooltip += `\nTime: ${timeRange}`
      }
      
      if (description) {
        tooltip += `\nDescription: ${description}`
      }
      
      return tooltip
    },
    previousMonth() {
      if (this.currentMonth === 0) {
        this.currentMonth = 11
        this.currentYear--
      } else {
        this.currentMonth--
      }
      this.emitMonthChange()
    },
    nextMonth() {
      if (this.currentMonth === 11) {
        this.currentMonth = 0
        this.currentYear++
      } else {
        this.currentMonth++
      }
      this.emitMonthChange()
    },
    emitMonthChange() {
      this.$emit('month-change', {
        month: this.currentMonth,
        year: this.currentYear
      })
    },

    // New full calendar methods
    goToToday() {
      const today = new Date()
      this.currentMonth = today.getMonth()
      this.currentYear = today.getFullYear()
      this.selectedDate = today
      this.emitMonthChange()
    },

    selectDate(date) {
      this.selectedDate = date.dateObj
      if (this.currentView !== 'month') {
        this.currentView = 'day'
      }
    },

    handleDateClick(date) {
      // Don't allow clicks on past dates
      if (date.isPast) {
        return
      }
      
      // Select the date first
      this.selectDate(date)
      
      // If it's current month and no events, open create form
      if (!date.isOtherMonth) {
        this.$emit('add-event', {
          date: date.date,
          dateObj: date.dateObj
        })
      }
    },

    isDateSelected(date) {
      const selectedDateStr = `${this.selectedDate.getFullYear()}-${String(this.selectedDate.getMonth() + 1).padStart(2, '0')}-${String(this.selectedDate.getDate()).padStart(2, '0')}`
      return date.date === selectedDateStr
    },

    addEventToDate(date) {
      this.$emit('add-event', {
        date: date.date,
        dateObj: date.dateObj
      })
    },

    addEventToDateTime(date, hour) {
      this.$emit('add-event', {
        date: date.date,
        dateObj: date.dateObj,
        hour: hour,
        time: this.formatHour(hour)
      })
    },

    getEventsForDisplay(events) {
      return events.slice(0, this.getMaxEventsPerDay())
    },

    getMaxEventsPerDay() {
      // Adjust based on view and screen size
      if (window.innerWidth < 768) return 2
      return 4
    },

    getEventStyle(event) {
      // You can customize event colors based on event properties
      const colors = {
        high: '#dc3545',
        medium: '#ffc107', 
        low: '#28a745',
        normal: '#007bff'
      }
      
      const priority = event.priority || 'normal'
      return {
        backgroundColor: colors[priority] || colors.normal,
        borderLeft: `3px solid ${colors[priority] || colors.normal}`
      }
    },

    getGoogleEventStyle(event) {
      // Google Calendar style colors - ensure reactivity to color changes
      const eventColor = event.additionalProp1?.color || event.color || '#1a73e8'
      
      return {
        backgroundColor: eventColor,
        borderLeft: `4px solid ${this.darkenColor(eventColor, 20)}`,
        color: this.getContrastColor(eventColor)
      }
    },

    darkenColor(color, percent) {
      const num = parseInt(color.replace("#", ""), 16);
      const amt = Math.round(2.55 * percent);
      const R = (num >> 16) - amt;
      const G = (num >> 8 & 0x00FF) - amt;
      const B = (num & 0x0000FF) - amt;
      return "#" + (0x1000000 + (R < 255 ? R < 1 ? 0 : R : 255) * 0x10000 +
        (G < 255 ? G < 1 ? 0 : G : 255) * 0x100 +
        (B < 255 ? B < 1 ? 0 : B : 255)).toString(16).slice(1);
    },

    getContrastColor(hexColor) {
      // Convert hex to RGB
      const r = parseInt(hexColor.substr(1, 2), 16);
      const g = parseInt(hexColor.substr(3, 2), 16);
      const b = parseInt(hexColor.substr(5, 2), 16);
      
      // Calculate brightness
      const brightness = (r * 299 + g * 587 + b * 114) / 1000;
      
      return brightness > 128 ? '#000000' : '#ffffff';
    },

    showMoreEvents(date) {
      this.selectedEvent = null
      this.showEventModal = true
      // You could emit an event or show a modal with all events for this date
      this.$emit('show-more', {
        date: date,
        events: date.events
      })
    },

    previousDay() {
      const prevDay = new Date(this.selectedDate)
      prevDay.setDate(prevDay.getDate() - 1)
      this.selectedDate = prevDay
      this.updateMonthFromDate(prevDay)
    },

    nextDay() {
      const nextDay = new Date(this.selectedDate)
      nextDay.setDate(nextDay.getDate() + 1)
      this.selectedDate = nextDay
      this.updateMonthFromDate(nextDay)
    },

    updateMonthFromDate(date) {
      if (date.getMonth() !== this.currentMonth) {
        this.currentMonth = date.getMonth()
        this.currentYear = date.getFullYear()
        this.emitMonthChange()
      }
    },

    formatDayTitle(date) {
      return date.toLocaleDateString('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      })
    },

    formatDate(dateString) {
      if (!dateString) return ''
      const date = new Date(dateString)
      return date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      })
    },

    formatHour(hour) {
      if (hour === 0) return '12:00 AM'
      if (hour < 12) return `${hour}:00 AM`
      if (hour === 12) return '12:00 PM'
      return `${hour - 12}:00 PM`
    },

    formatTimeRange(startTime, endTime) {
      const times = []
      if (startTime) times.push(this.formatTime(startTime))
      if (endTime) times.push(this.formatTime(endTime))
      return times.join(' - ') || 'All day'
    },

    isCurrentHour(hour, date) {
      const now = new Date()
      const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`
      const currentHour = now.getHours()
      
      return date.date === today && hour === currentHour
    },

    getEventsForTimeSlot(events, hour) {
      return events.filter(event => {
        const startTime = event.additionalProp1?.start_time
        if (!startTime) return hour === 0 // All-day events show at midnight
        
        const eventHour = parseInt(startTime.split(':')[0])
        return eventHour === hour
      })
    },

    getEventsForDate(date) {
      const dateStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
      return this.eventsGroupedByDate[dateStr] || []
    },

    // Modal methods
    closeEventModal() {
      this.showEventModal = false
      this.selectedEvent = null
    },

    editEvent(event) {
      this.$emit('edit-event', event)
      this.closeEventModal()
    },

    // Event click handler with drag detection
    handleEventClick(event, date) {
      // Add a small delay to allow drag to be detected
      setTimeout(() => {
        if (!this.isDragging) {
          this.$emit('event-click', event, date)
        }
      }, 100)
    },

    // Handle clicks on event items, allowing links to work
    handleEventItemClick(clickEvent, event, date) {
      // Check if the click target is a link or inside a link
      const target = clickEvent.target
      const isLink = target.tagName === 'A' || target.closest('a')
      
      // If it's a link, let the default behavior happen (open the link)
      if (isLink) {
        return
      }
      
      // Otherwise, stop propagation and handle as event click
      clickEvent.stopPropagation()
      this.handleEventClick(event, date)
    },

    // Drag and Drop methods
    handleDragStart(event, dragEvent) {
      console.log('DRAG START:', event)
      this.isDragging = true
      this.draggedEvent = event
      
      // Set the drag data
      dragEvent.dataTransfer.effectAllowed = 'move'
      dragEvent.dataTransfer.setData('text/plain', JSON.stringify({
        eventId: event.id,
        eventTitle: event.title
      }))
      
      // Store event data for cross-browser compatibility
      window.draggedEventData = event
      
      // Prevent event click from firing
      dragEvent.stopPropagation()
    },

    handleDragEnd() {
      console.log('DRAG END')
      // Clean up
      this.draggedEvent = null
      window.draggedEventData = null
      
      // Reset drag flag after a short delay
      setTimeout(() => {
        this.isDragging = false
      }, 150)
    },

    handleDragOver(date, dragEvent) {
      if (!this.draggedEvent || date.isOtherMonth || date.isPast) {
        dragEvent.dataTransfer.dropEffect = 'none'
        return
      }
      dragEvent.dataTransfer.dropEffect = 'move'
    },

    handleDragEnter(date, dragEvent) {
      if (!this.draggedEvent || date.isOtherMonth || date.isPast) return
      
      const cell = dragEvent.currentTarget
      if (cell && cell.classList.contains('date-cell')) {
        cell.classList.add('drag-over')
      }
    },

    handleDragLeave(date, dragEvent) {
      const cell = dragEvent.currentTarget
      if (cell && cell.classList.contains('date-cell')) {
        cell.classList.remove('drag-over')
      }
    },

    handleDrop(targetDate, dropEvent) {
      dropEvent.preventDefault()
      dropEvent.stopPropagation()
      
      console.log('DROP EVENT - Start')
      
      // Remove drag over styling
      const cell = dropEvent.currentTarget
      if (cell && cell.classList.contains('date-cell')) {
        cell.classList.remove('drag-over')
      }
      
      // Get the dragged event from component data or window fallback
      const draggedEvent = this.draggedEvent || window.draggedEventData
      
      console.log('DROP EVENT:', {
        draggedEvent: draggedEvent,
        targetDate: targetDate,
        isOtherMonth: targetDate.isOtherMonth
      })
      
      if (!draggedEvent || targetDate.isOtherMonth || targetDate.isPast) {
        console.log('Drop cancelled: no dragged event, other month, or past date')
        return
      }

      // Only proceed if the date is different
      const originalDate = draggedEvent.additionalProp1?.date || draggedEvent.date
      if (originalDate === targetDate.date) {
        console.log('Drop cancelled: same date')
        return
      }

      console.log('Emitting event-drag:', {
        event: draggedEvent,
        newDate: targetDate.date,
        originalDate: originalDate
      })

      // Immediately move the event visually for instant feedback
      this.$emit('immediate-move', {
        event: draggedEvent,
        newDate: targetDate.date,
        originalDate: originalDate
      })

      // Then emit the API call event
      this.$emit('event-drag', {
        event: draggedEvent,
        newDate: targetDate.date,
        originalDate: originalDate
      })
    }
  },
  watch: {
    month(newMonth) {
      this.currentMonth = newMonth
    },
    year(newYear) {
      this.currentYear = newYear
    },
    pages: {
      handler() {
        // Force re-computation of calendar dates when pages data changes
        this.$nextTick(() => {
          this.$forceUpdate()
        })
      },
      deep: true
    }
  },
  mounted() {
    // Component is ready
  }
}
</script>

<style scoped>
/* Google Calendar Styles */
.full-calendar {
  position: relative;
  font-family: 'Google Sans', 'Roboto', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  max-width: 100%;
  margin: 0 auto;
  background: #ffffff;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(60,64,67,0.3);
  border: 1px solid #dadce0;
}

/* Google Calendar Toolbar */
.google-calendar-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 24px;
  border-bottom: 1px solid #dadce0;
  background: #ffffff;
  border-radius: 8px 8px 0 0;
  min-height: 64px;
}

.toolbar-left {
  display: flex;
  align-items: center;
  gap: 16px;
}

.toolbar-right {
  display: flex;
  align-items: center;
}

.create-btn {
  background: #1a73e8;
  color: white;
  border: none;
  border-radius: 24px;
  padding: 10px 24px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  box-shadow: 0 1px 2px rgba(60,64,67,0.3), 0 1px 3px 1px rgba(60,64,67,0.15);
  transition: all 0.2s ease;
}

.create-btn:hover:not(:disabled) {
  background: #1557b0;
  box-shadow: 0 1px 3px rgba(60,64,67,0.3), 0 4px 8px 3px rgba(60,64,67,0.15);
}

.create-btn:disabled {
  background: #f1f3f4;
  color: #9aa0a6;
  cursor: not-allowed;
  box-shadow: none;
}

.today-btn {
  background: transparent;
  color: #3c4043;
  border: 1px solid #dadce0;
  border-radius: 4px;
  padding: 8px 16px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.today-btn:hover:not(:disabled) {
  background: #f8f9fa;
  border-color: #dadce0;
}

.nav-controls {
  display: flex;
  gap: 4px;
}

.nav-btn {
  background: transparent;
  border: none;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #5f6368;
  transition: all 0.2s ease;
}

.nav-btn:hover:not(:disabled) {
  background: #f1f3f4;
}

.nav-btn:disabled {
  opacity: 0.38;
  cursor: not-allowed;
}

.calendar-title {
  font-size: 22px;
  font-weight: 400;
  color: #3c4043;
  margin: 0;
  letter-spacing: 0;
}

.view-selector {
  display: flex;
  border: 1px solid #dadce0;
  border-radius: 4px;
  overflow: hidden;
}

.view-btn {
  background: transparent;
  border: none;
  padding: 8px 16px;
  font-size: 14px;
  color: #3c4043;
  cursor: pointer;
  transition: all 0.2s ease;
  border-right: 1px solid #dadce0;
}

.view-btn:last-child {
  border-right: none;
}

.view-btn:hover {
  background: #f8f9fa;
}

.view-btn.active {
  background: #e8f0fe;
  color: #1a73e8;
  font-weight: 500;
}

/* Google Calendar Month View */
.month-view {
  padding: 0;
  background: #ffffff;
}

.google-weekdays-header {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  border-bottom: 1px solid #dadce0;
  background: #ffffff;
}

.google-weekday-cell {
  padding: 16px 8px;
  text-align: center;
  border-right: 1px solid #dadce0;
}

.google-weekday-cell:last-child {
  border-right: none;
}

.weekday-label {
  font-size: 11px;
  font-weight: 500;
  color: #70757a;
  text-transform: uppercase;
  letter-spacing: 0.8px;
}

.calendar-grid {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  grid-template-rows: repeat(6, 1fr);
  background: #ffffff;
  min-height: 720px;
}

.date-cell {
  background: #ffffff;
  min-height: 120px;
  position: relative;
  cursor: pointer;
  transition: all 0.15s ease;
  border-right: 1px solid #dadce0;
  border-bottom: 1px solid #dadce0;
}

.date-cell:hover {
  background: #f8f9fa;
}

.date-cell:nth-child(7n) {
  border-right: none;
}

.date-cell.other-month {
  background: #ffffff;
}

.date-cell.other-month .google-day-number {
  color: #9aa0a6;
  opacity: 0.6;
}

.date-cell.other-month .google-events-container {
  opacity: 0.7;
}

.date-cell.today {
  background: #ffffff;
}

.date-cell.past {
  background: #f8f9fa;
  cursor: not-allowed;
  pointer-events: none;
  opacity: 0.6;
}

.date-cell.past .google-day-number {
  color: #9aa0a6;
}

.date-cell.past .google-events-container {
  opacity: 0.5;
}

.date-cell.selected {
  background: #e8f0fe;
}

.date-cell.weekend {
  background: #ffffff;
}

.google-day-header {
  padding: 8px 12px;
  display: flex;
  align-items: center;
  justify-content: flex-start;
}

.google-day-number {
  font-size: 14px;
  font-weight: 400;
  color: #3c4043;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: all 0.15s ease;
}

.google-day-number.today-number {
  background: #1a73e8;
  color: #ffffff;
  font-weight: 500;
}

.google-events-container {
  padding: 0 8px 8px 8px;
  display: flex;
  flex-direction: column;
  gap: 2px;
  max-height: 80px;
  overflow: hidden;
}

.google-event-item {
  background: #1a73e8;
  color: #ffffff;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.15s ease;
  overflow: hidden;
  position: relative;
  min-height: 20px;
  display: flex;
  align-items: center;
}

.google-event-item:hover {
  opacity: 0.9;
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.google-event-item[draggable="true"] {
  cursor: grab;
  user-select: none;
}

.google-event-item[draggable="true"]:active {
  cursor: grabbing;
}

.date-cell.drag-over {
  background: #e8f0fe !important;
  border: 2px dashed #1a73e8 !important;
  transform: scale(1.02);
  transition: all 0.2s ease;
}

.google-event-item.dragging {
  opacity: 0.5;
  z-index: 1000;
  transform: rotate(5deg);
}

.google-event-item.inactive {
  opacity: 0.6;
}

.google-event-content {
  display: flex;
  align-items: center;
  gap: 4px;
  width: 100%;
  overflow: hidden;
}

.google-event-time {
  font-size: 11px;
  font-weight: 500;
  opacity: 0.9;
  flex-shrink: 0;
}

.google-event-title {
  font-size: 12px;
  font-weight: 400;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex: 1;
}

.google-more-events {
  color: #5f6368;
  font-size: 11px;
  padding: 2px 8px;
  cursor: pointer;
  border-radius: 4px;
  transition: all 0.15s ease;
}

.google-more-events:hover {
  background: #f1f3f4;
}

.day-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem;
  border-bottom: 1px solid #f1f1f1;
}

.day-number {
  font-weight: 600;
  font-size: 1rem;
  color: #2c3e50;
}

.date-cell.today .day-number {
  background: #2196f3;
  color: white;
  border-radius: 50%;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
}

.add-event-btn {
  background: transparent;
  border: none;
  color: #007bff;
  cursor: pointer;
  padding: 4px;
  border-radius: 50%;
  opacity: 0;
  transition: all 0.2s ease;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.date-cell:hover .add-event-btn {
  opacity: 1;
}

.add-event-btn:hover {
  background: #007bff;
  color: white;
}

.events-container {
  padding: 0 0.5rem 0.5rem;
  display: flex;
  flex-direction: column;
  gap: 2px;
  max-height: 60px;
  overflow-y: auto;
}

.event-item {
  background: #007bff;
  color: white;
  padding: 4px 6px;
  border-radius: 4px;
  font-size: 0.75rem;
  cursor: pointer;
  transition: all 0.2s ease;
  overflow: hidden;
  border-left: 4px solid rgba(255,255,255,0.3);
}

.event-item:hover {
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(0,0,0,0.2);
}

.event-item.inactive {
  background: #6c757d;
  opacity: 0.7;
}

.event-priority-high {
  background: #dc3545 !important;
}

.event-priority-medium {
  background: #ffc107 !important;
  color: #212529 !important;
}

.event-priority-low {
  background: #28a745 !important;
}

.event-content {
  display: flex;
  flex-direction: column;
}

.event-time {
  font-weight: 600;
  font-size: 0.7rem;
  opacity: 0.9;
  margin-bottom: 1px;
}

.event-title {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.2;
  font-weight: 500;
}

.more-events {
  background: linear-gradient(135deg, #28a745, #20c997);
  color: white;
  padding: 4px 6px;
  border-radius: 4px;
  font-size: 0.7rem;
  cursor: pointer;
  text-align: center;
  font-weight: 600;
  transition: all 0.2s ease;
}

.more-events:hover {
  background: linear-gradient(135deg, #1e7e34, #198754);
  transform: translateY(-1px);
}

/* Week View */
.week-view {
  padding: 0;
}

.week-header {
  display: grid;
  grid-template-columns: 60px repeat(7, 1fr);
  background: #f8f9fa;
  border-bottom: 1px solid #dee2e6;
}

.time-gutter {
  background: #f8f9fa;
  border-right: 1px solid #dee2e6;
}

.week-day-header {
  padding: 1rem 0.5rem;
  text-align: center;
  border-right: 1px solid #dee2e6;
}

.week-day-header.today {
  background: #e3f2fd;
  font-weight: 600;
}

.week-day-name {
  font-size: 0.875rem;
  font-weight: 600;
  color: #495057;
  text-transform: uppercase;
}

.week-day-number {
  font-size: 1.25rem;
  font-weight: 700;
  color: #2c3e50;
  margin-top: 0.25rem;
}

.week-grid {
  display: grid;
  grid-template-columns: 60px repeat(7, 1fr);
  max-height: 600px;
  overflow-y: auto;
}

.time-slots {
  background: #f8f9fa;
  border-right: 1px solid #dee2e6;
}

.time-slot {
  height: 60px;
  padding: 0.25rem;
  border-bottom: 1px solid #e9ecef;
  display: flex;
  align-items: flex-start;
  justify-content: center;
}

.time-label {
  font-size: 0.75rem;
  color: #6c757d;
  font-weight: 500;
}

.week-days {
  display: contents;
}

.week-day-column {
  border-right: 1px solid #e9ecef;
}

.time-slot-cell {
  height: 60px;
  border-bottom: 1px solid #f1f1f1;
  position: relative;
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.time-slot-cell:hover {
  background: #f8f9fa;
}

.time-slot-cell.current-hour {
  background: #fff3cd;
}

.week-event-item, .day-event-item {
  position: absolute;
  left: 2px;
  right: 2px;
  top: 2px;
  background: #007bff;
  color: white;
  padding: 2px 4px;
  border-radius: 3px;
  font-size: 0.7rem;
  cursor: pointer;
  overflow: hidden;
  z-index: 2;
}

.week-event-content, .day-event-content {
  display: flex;
  flex-direction: column;
}

.week-event-time, .day-event-time {
  font-weight: 600;
  font-size: 0.65rem;
  opacity: 0.9;
}

.week-event-title, .day-event-title {
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Day View */
.day-view {
  padding: 0;
}

.day-header {
  background: #f8f9fa;
  padding: 1rem 1.5rem;
  border-bottom: 1px solid #dee2e6;
}

.day-navigation {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
}

.day-title {
  font-size: 1.5rem;
  font-weight: 600;
  color: #2c3e50;
  margin: 0;
  min-width: 300px;
  text-align: center;
}

.day-grid {
  display: grid;
  grid-template-columns: 80px 1fr;
  max-height: 700px;
  overflow-y: auto;
}

.day-column {
  border-right: 1px solid #e9ecef;
}

.day-event-description {
  font-size: 0.65rem;
  opacity: 0.9;
  margin-top: 2px;
  line-height: 1.2;
}

/* Links in event descriptions */
.day-event-description a,
.event-detail a {
  color: #1a73e8;
  text-decoration: underline;
  cursor: pointer;
}

.day-event-description a:hover,
.event-detail a:hover {
  color: #0d47a1;
  text-decoration: underline;
}


/* Loading State */
.loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.9);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 100;
  border-radius: 8px;
}

.loading-content {
  text-align: center;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #007bff;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 1rem;
}

.loading-text {
  color: #495057;
  font-weight: 500;
  margin: 0;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* Event Modal */
.event-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.event-modal {
  background: white;
  border-radius: 8px;
  max-width: 500px;
  width: 90vw;
  max-height: 80vh;
  overflow-y: auto;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 1.5rem;
  border-bottom: 1px solid #e9ecef;
}

.modal-title {
  font-size: 1.25rem;
  font-weight: 600;
  margin: 0;
}

.close-btn {
  background: none;
  border: none;
  font-size: 1.25rem;
  cursor: pointer;
  color: #6c757d;
  padding: 0;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.modal-body {
  padding: 1.5rem;
}

.event-detail {
  margin-bottom: 1rem;
  line-height: 1.5;
}

.modal-footer {
  padding: 1rem 1.5rem;
  border-top: 1px solid #e9ecef;
  display: flex;
  justify-content: flex-end;
}

/* Responsive Design */
@media (max-width: 768px) {
  .calendar-toolbar {
    flex-direction: column;
    gap: 0.5rem;
    padding: 0.75rem;
  }
  
  .toolbar-left, .toolbar-right {
    width: 100%;
    justify-content: center;
  }
  
  .calendar-title {
    font-size: 1.25rem;
  }
  
  .weekday-name {
    display: none;
  }
  
  .weekday-short {
    display: block;
  }
  
  .weekday-cell {
    padding: 0.5rem 0.25rem;
  }
  
  .date-cell {
    min-height: 80px;
    max-height: 90px;
  }
  
  .events-container {
    max-height: 40px;
    padding: 0 0.25rem 0.25rem;
  }
  
  .event-item {
    font-size: 0.65rem;
    padding: 1px 3px;
  }
  
  .week-grid, .day-grid {
    max-height: 400px;
  }
  
  .time-slots {
    grid-column: span 8;
    display: none;
  }
  
  .week-days {
    grid-column: span 8;
  }
}

@media (max-width: 480px) {
  .weekday-cell {
    padding: 0.5rem 0.25rem;
  }
  
  .date-cell {
    min-height: 80px;
  }
  
  .day-header {
    padding: 0.75rem;
  }
  
  .event-item {
    font-size: 0.65rem;
    padding: 1px 3px;
  }
  
  .calendar-title {
    font-size: 1.25rem;
  }
}
</style>